import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { Quote } from 'lucide-react';


/**
 * Ишлэл — нүүр хуудасны section.
 * Нэг дор нэг ишлэл харагдана, автоматаар солигдоно (auto-slide),
 * мөн гар утсан дээр swipe (зүүн/баруун) хийж болно.
 */
export default function QuotesSection({ quotes = [] }) {
    const lang = useSelector((s) => s.system.lang || 'mn');
    const [active, setActive] = useState(0);
    const paused = useRef(false);
    const dragX = useRef(null);

    const count = quotes?.length || 0;

    const goNext = useCallback(() => {
        if (count === 0) return;
        setActive((p) => (p + 1) % count);
    }, [count]);

    const goPrev = useCallback(() => {
        if (count === 0) return;
        setActive((p) => (p - 1 + count) % count);
    }, [count]);

    // Auto-slide
    useEffect(() => {
        if (count <= 1) return undefined;
        const id = setInterval(() => {
            if (!paused.current) setActive((p) => (p + 1) % count);
        }, 5000);
        return () => clearInterval(id);
    }, [count]);

    // Swipe / drag
    const onStart = (x) => {
        dragX.current = x;
        paused.current = true;
    };
    const onEnd = (x) => {
        if (dragX.current != null) {
            const dx = x - dragX.current;
            if (Math.abs(dx) > 50) {
                if (dx < 0) goNext();
                else goPrev();
            }
        }
        dragX.current = null;
        paused.current = false;
    };

    if (!quotes || count === 0) return null;

    const current = quotes[active];
    const name = lang === 'en' && current.nameEn ? current.nameEn : current.name;
    const position =
        lang === 'en' && current.positionEn
            ? current.positionEn
            : current.position;
    const quote =
        lang === 'en' && current.quoteEn ? current.quoteEn : current.quote;
    const imageUrl = current.imageUrl || '/placeholders/avatar-default.svg';

    return (
        <section className="py-20 lg:py-28 bg-white">
            <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
                <div
                    className="max-w-3xl mx-auto text-center select-none"
                    onTouchStart={(e) => onStart(e.touches[0].clientX)}
                    onTouchEnd={(e) => onEnd(e.changedTouches[0].clientX)}
                    onMouseDown={(e) => onStart(e.clientX)}
                    onMouseUp={(e) => onEnd(e.clientX)}
                    onMouseEnter={() => {
                        paused.current = true;
                    }}
                    onMouseLeave={() => {
                        paused.current = false;
                    }}
                >
                    <div key={current.id} className="animate-fade-in">
                        <Quote
                            size={48}
                            className="text-brand-yellow mx-auto mb-6"
                            strokeWidth={1}
                        />
                        <blockquote className="text-xl lg:text-3xl leading-relaxed text-brand-dark font-medium mb-10">
                            “{quote}”
                        </blockquote>

                        <div className="flex flex-col items-center gap-3">
                            <div className="relative w-16 h-16 rounded-full overflow-hidden bg-brand-cream-dark">
                                <Image
                                    src={imageUrl}
                                    alt={name}
                                    fill
                                    sizes="64px"
                                    className="object-cover"
                                    unoptimized
                                />
                            </div>
                            <div>
                                <p className="font-heading uppercase font-bold text-lg text-brand-dark">
                                    {name}
                                </p>
                                {position && (
                                    <p className="text-sm text-brand-yellow-dark mt-0.5">
                                        {position}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Цэгүүд (индикатор) */}
                    {count > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-10">
                            {quotes.map((q, i) => (
                                <button
                                    key={q.id}
                                    onClick={() => setActive(i)}
                                    aria-label={`Quote ${i + 1}`}
                                    className={`h-2 rounded-full transition-all ${
                                        i === active
                                            ? 'w-8 bg-brand-yellow'
                                            : 'w-2 bg-brand-cream-dark hover:bg-brand-gray-light'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}